/**
 * Phone view API seam (slice 19 `web-phone-pwa`). Owner: web-phone-pwa.
 *
 * These types are the subset of `packages/contracts/openapi.json` the phone view reads, written by hand
 * because the generated client (`src/lib/api/generated`, slice 14 `web-shell`) is not on main yet. W2 swaps
 * each type for its generated twin; the field names are the contract's, so no component changes.
 *
 * Rules this module enforces:
 * - GETs only read. Every write is a POST/PUT with the `X-CSRF-Token` double-submit header (auth/sessions.py).
 * - No offline writes (scope §5.11): a write while `navigator.onLine` is false throws `offline` before any
 *   request is built, so nothing is queued and nothing is replayed later.
 * - Every request has a timeout. A failed lookup is an error the UI renders as "unreachable", never as empty.
 */
import { queryOptions } from '@tanstack/react-query'
import { describeError } from '../../lib/api/client'
import type { PostSendPlan } from '../../lib/api/generated'

export { describeError }

// ---- contract types (openapi.json components/schemas) --------------------------------------------------

export type Role = 'manager' | 'cover' | 'tech' | 'billing'
export type DeskTokenState = 'connected' | 'refresh_failing' | 'not_connected'
export type OwnerKind = 'us' | 'customer' | 'contractor' | 'vendor'
export type Audience = 'customer' | 'contractor' | 'vendor' | 'internal' | 'unclassified'
export type SendBox = 'enabled' | 'withheld' | 'connect_desk' | 'mirror_stale' | 'guard_unavailable'
export type LaneKey = 'reply_owed' | 'action_owed' | 'waiting_customer' | 'waiting_contractor' | 'scheduled'
/** Later is a queue lane, not a conversation state (#549). My day still loads it so a future move stays on the phone. */
export type MyDayLane = LaneKey | 'later'
export type ConversationState = LaneKey | 'closed'
export type TimeSource = 'manual' | 'send_prompt' | 'close_prompt' | 'work_handoff' | 'capture' | 'packet' | 'mobile' | 'estimate_accepted'

export interface UserRef {
  id: string
  name: string
  email?: string | null
}

export interface MeOut {
  id: string
  email: string
  name: string
  role: Role
  ui_mode: 'full' | 'simple'
  desk_token: DeskTokenState
  can_write_desk: boolean
  csrf_token: string
  desk_agent_id?: string | null
}

export interface NextMoveOut {
  id: string
  text: string
  owner_kind: OwnerKind
  state: string
  proposed_by: string
  due_at?: string | null
  overdue?: boolean
  owner?: UserRef | null
  owner_contractor_id?: string | null
}

export interface TicketChip {
  desk_ticket_id: string
  ticket_number: string
  role: string
  link_source: string
  desk_status?: string | null
  tombstoned?: boolean
}

export interface RileyCaseSummary {
  headline: string
  problem: string
  tried: string[]
  status: string
  waiting_on: string
  next: string
  as_of: string
}

export interface CaseRow {
  id: string
  number: string
  title: string
  identity_state: string
  tickets: TicketChip[]
  conversation_state: ConversationState
  ledger_state: string
  customer_display?: string | null
  draft_ready?: boolean
  flags?: string[]
  last_activity_at?: string | null
  next_move?: NextMoveOut | null
  owner?: UserRef | null
  summary_line?: string | null
  case_summary?: RileyCaseSummary | null
}

export interface PageCaseRow {
  items: CaseRow[]
  next_cursor?: string | null
  total?: number | null
}

export interface CaseDetail extends CaseRow {
  site_address?: string | null
  symptom_first?: string | null
  symptom_latest?: string | null
  merged_into?: string | null
}

export interface ThreadOut {
  id: string
  audience: Audience
  has_engagement: boolean
  display_name_internal?: string | null
  party_ref?: string | null
  unread?: number
}

export interface TurnOut {
  id: string
  thread_id: string
  direction: 'in' | 'out' | 'draft' | 'note'
  author_kind: 'customer' | 'contractor' | 'vendor' | 'staff' | 'riley'
  sent_via: string
  audience_source: string
  author_ref?: string | null
  from_addr?: string | null
  staff_delivery_note?: string | null
  sent_at?: string | null
  text_clean?: string | null
  /** zuid -> name for each Desk mention in text_clean (#113). */
  mentions?: Record<string, string>
  is_auto_reply?: boolean
  auto_reply_until?: string | null
  is_unsent_draft?: boolean
  attachments?: Record<string, unknown>[]
}

export interface ThreadTurns {
  thread: ThreadOut
  turns: TurnOut[]
  as_of?: string | null
}

export interface SendState {
  send_box: SendBox
  audience: Audience
  reason?: string | null
  target_ticket_id?: string | null
  target_ticket_number?: string | null
  visibility?: 'public_customer' | 'public_external' | 'none'
  signature_html?: string | null
  quote_default?: boolean
}

export interface WithheldReason {
  code: string
  message: string
  field?: string | null
  severity?: 'block' | 'warn'
}

export interface GuardVerdict {
  allowed: boolean
  phase: 'compose' | 'send'
  send_box: SendBox
  send_box_reason?: string | null
  to?: string[]
  cc?: string[]
  removed_recipients?: Record<string, string>[]
  scrub_report?: Record<string, number>
  scrubbed_body_html?: string | null
  warnings?: WithheldReason[]
  withheld_reasons?: WithheldReason[]
  decision_id?: string | null
  reply_intent?: 'close' | 'ask' | 'update' | 'resolve'
  asks_customer?: boolean
}

export type TimeDomain = 'phone' | 'it' | 'macd'

/** Round 13: one domain's billable default. `hourly_rate` arrives only for money roles. */
export interface TimeDomainDefault {
  domain: TimeDomain
  decision: string
  billable_default: boolean
  basis: string
  hourly_rate?: string | number | null
}

export interface TimeDefaultsOut {
  domain: TimeDomain
  domain_source: string
  domains: TimeDomainDefault[]
}

export interface TimePromptOut {
  prompt_ref: string
  default_minutes?: number | null
  default_source?: string | null
  billable_default?: boolean | null
  billable_basis?: string | null
  domain?: TimeDomain | null
  domains?: TimeDomainDefault[]
}

export interface SendOut {
  desk_thread_id: string
  sent_at: string
  via: 'sendReply' | 'sendDraft'
  riley_draft: string
  guard_decision_id: string
  time_prompt: TimePromptOut
}

export interface TimeEntryIn {
  case_id: string
  desk_ticket_id: string
  minutes?: number
  billable?: boolean
  no_time?: boolean
  no_time_reason?: string | null
  note?: string | null
  prompt_ref?: string | null
  source?: TimeSource
  /** #186: the tracked seconds this entry accounts for. */
  tracked_seconds?: number | null
  domain?: TimeDomain | null
}

export interface TimeEntryOut {
  id: string
  case_id: string
  minutes: number
  is_no_time: boolean
  desk_sync_state: 'synced' | 'pending_token' | 'failed'
  source: TimeSource
}

export interface NextMoveIn {
  text: string
  owner_kind: OwnerKind
  due_at: string
  owner_user_id?: string | null
}

export type PartSwapAction = 'added' | 'replaced' | 'removed'
export interface PartsSwappedIn {
  item_key: string
  action: PartSwapAction
  hardware_unit_id: string | null
  replaces_component_id: string | null
  removed_name: string | null
  removed_serial_number: string | null
  installed_name: string | null
  installed_kind: string | null
  installed_serial_number: string | null
  firmware_version: string | null
  warranty_start: string | null
  warranty_end: string | null
  occurred_on: string
  reason: string | null
  rma_required: boolean
}

export interface LivenessOut {
  ok: boolean
  mirror_state: 'ok' | 'amber' | 'red' | 'unknown'
  sends_disabled: boolean
  sends_disabled_reason?: string | null
  outbox_pending: number
  version: string
}

export interface SendIn {
  thread_id: string
  body_html: string
  to: string[]
  cc?: string[]
  idempotency_key: string
  /** #746: the post-send chip row, applied by the server only after Desk reports the reply delivered. */
  post_send?: PostSendPlan | null
}

// ---- transport ----------------------------------------------------------------------------------------

export const API_BASE = '/api/v1'
const TIMEOUT_MS = 10_000

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/** A read and when the server produced it. A response the service worker served from cache keeps its old Date. */
export interface Served<T> {
  data: T
  servedAt: number | null
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  for (const part of document.cookie.split(';')) {
    const [k, ...v] = part.trim().split('=')
    if (k === name) return decodeURIComponent(v.join('='))
  }
  return null
}

let csrfFromMe: string | null = null
/** /me echoes the CSRF token; the `pb_csrf` cookie wins when it is readable. */
export function rememberCsrf(token: string | null | undefined) {
  csrfFromMe = token ?? null
}

async function failure(res: Response): Promise<ApiError> {
  let code = `http_${res.status}`
  let detail = res.statusText || `HTTP ${res.status}`
  try {
    const body = (await res.json()) as { code?: unknown; detail?: unknown }
    if (typeof body.code === 'string') code = body.code
    if (typeof body.detail === 'string') detail = body.detail
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, code, detail)
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { Accept: 'application/json', ...(init.headers ?? {}) },
      signal: init.signal ?? AbortSignal.timeout(TIMEOUT_MS),
    })
  } catch (e) {
    throw new ApiError(0, isOnline() ? 'unreachable' : 'offline', e instanceof Error ? e.message : String(e))
  }
  if (!res.ok) throw await failure(res)
  return res
}

export async function getJson<T>(path: string): Promise<Served<T>> {
  const res = await request(path)
  const date = res.headers.get('date')
  const servedAt = date ? Date.parse(date) : Number.NaN
  return { data: (await res.json()) as T, servedAt: Number.isNaN(servedAt) ? null : servedAt }
}

export async function writeJson<T>(method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown, timeoutMs = TIMEOUT_MS): Promise<T> {
  // No offline writes: refuse before building the request so nothing can be queued or replayed.
  if (!isOnline()) throw new ApiError(0, 'offline', 'You are offline. Nothing was saved.')
  const csrf = readCookie('pb_csrf') ?? csrfFromMe ?? ''
  const res = await request(path, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function writeFile<T>(path: string, file: File, idempotencyKey: string): Promise<T> {
  if (!isOnline()) throw new ApiError(0, 'offline', 'You are offline. Nothing was saved.')
  if (file.size > 25 * 1024 * 1024) throw new ApiError(413, 'file_too_large', 'Photos are limited to 25 MB.')
  const csrf = readCookie('pb_csrf') ?? csrfFromMe ?? ''
  const res = await request(path, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-CSRF-Token': csrf,
      'Idempotency-Key': idempotencyKey,
      'X-Filename': encodeURIComponent(file.name || 'photo'),
    },
    body: file,
  })
  return (await res.json()) as T
}

export function uuid(): string {
  const c: Crypto = globalThis.crypto
  if (typeof c.randomUUID === 'function') return c.randomUUID()
  // Older iOS Safari: RFC 4122 v4 from getRandomValues.
  const b = c.getRandomValues(new Uint8Array(16))
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** One recorded parts-swapped row (`PartSwapOut`); `replayed` is true when the same item_key was already saved. */
export interface PartsSwappedOut {
  id: string
  work_order_id: string
  item_key: string
  action: PartSwapAction
  source: 'portal' | 'staff'
  replayed: boolean
}

/** Parts are recorded on a work order, not a case: a case can hold several visits. */
export async function savePartsSwapped(workOrderId: string, body: PartsSwappedIn): Promise<PartsSwappedOut> {
  return writeJson<PartsSwappedOut>('POST', `/writeback/work-orders/${encodeURIComponent(workOrderId)}/parts-swapped`, body)
}

/** The statuses the staff parts-swapped route accepts (approved/revoked/expired take no more entries). */
export const PARTS_OPEN_WO_STATUSES = ['draft', 'sent', 'in_progress', 'submitted', 'returned'] as const
export const PHOTO_WO_STATUSES = ['sent', 'in_progress', 'submitted', 'returned', 'approved'] as const

/** The slice of `WorkOrderSummaryOut` the phone needs to pick which visit the parts belong to. */
export interface WorkOrderChoice {
  id: string
  display_number: string | null
  status: (typeof PARTS_OPEN_WO_STATUSES)[number] | (typeof PHOTO_WO_STATUSES)[number]
  assignee_kind: 'contractor' | 'internal_tech'
  scheduled_start: string | null
}

export interface MobileHardwareUnit {
  id: string
  order_line_id: string
  model?: string | null
  serial?: string | null
  mac?: string | null
  installed_wo_id?: string | null
}

export interface MobileHardwareLine {
  id: string
  product_name: string
  model?: string | null
}

export interface CaseHardware {
  case_id: string
  orders: { id: string; lines: MobileHardwareLine[]; units?: MobileHardwareUnit[] }[]
}

export interface WorkOrderPhotoOut {
  id: string
  work_order_id: string
  filename: string
  content_type: string
  size_bytes: number
  app_url: string
  replayed: boolean
}

export async function assignHardwareUnit(unitId: string, workOrderId: string): Promise<MobileHardwareUnit> {
  return writeJson<MobileHardwareUnit>('POST', `/procurement/units/${encodeURIComponent(unitId)}/assign`, {
    work_order_id: workOrderId,
  })
}

export async function uploadWorkOrderPhoto(
  caseId: string,
  workOrderId: string,
  file: File,
  idempotencyKey: string,
): Promise<WorkOrderPhotoOut> {
  return writeFile<WorkOrderPhotoOut>(
    `/procurement/work-orders/${encodeURIComponent(workOrderId)}/photos?case_id=${encodeURIComponent(caseId)}`,
    file,
    idempotencyKey,
  )
}

export async function postPhotoNote(
  caseId: string,
  body: { body_text: string; desk_ticket_id?: string | null; idempotency_key: string },
): Promise<void> {
  await writeJson('POST', `/cases/${encodeURIComponent(caseId)}/notes`, body)
}

// ---- reads (query options; keys follow the SSE invalidation keys: ['case', id], ['queue', lane] ...) ------

/** The lanes a tech's own cases can sit in. `no_time_logged` etc. are views over these, not extra cases. */
export const MY_DAY_LANES: MyDayLane[] = ['reply_owed', 'action_owed', 'waiting_customer', 'waiting_contractor', 'scheduled', 'later']

// Reads go to the network first and fall back to the service worker's copy offline (offlineFirst: the
// request is always attempted, so the SW can answer it; TanStack's default would pause it forever).
const read = { networkMode: 'offlineFirst' as const, retry: 1 }

export const openWorkOrdersQuery = (caseId: string) =>
  queryOptions({
    ...read,
    queryKey: ['case', caseId, 'work-orders', 'open'],
    queryFn: () => {
      const qs = new URLSearchParams({ case_id: caseId })
      for (const status of PARTS_OPEN_WO_STATUSES) qs.append('status', status)
      return getJson<{ items: WorkOrderChoice[] }>(`/work-orders?${qs.toString()}`)
    },
  })

export const photoWorkOrdersQuery = (caseId: string) =>
  queryOptions({
    ...read,
    queryKey: ['case', caseId, 'work-orders', 'photo'],
    queryFn: () => {
      const qs = new URLSearchParams({ case_id: caseId })
      for (const status of PHOTO_WO_STATUSES) qs.append('status', status)
      return getJson<{ items: WorkOrderChoice[] }>(`/work-orders?${qs.toString()}`)
    },
  })

export const caseHardwareQuery = (caseId: string) =>
  queryOptions({
    ...read,
    queryKey: ['procurement', 'case', caseId],
    queryFn: () => getJson<CaseHardware>(`/procurement/cases/${encodeURIComponent(caseId)}`),
  })

export const meQuery = () => queryOptions({ ...read, queryKey: ['me'], queryFn: () => getJson<MeOut>('/me') })

export const livenessQuery = () =>
  queryOptions({ ...read, queryKey: ['liveness'], queryFn: () => getJson<LivenessOut>('/liveness'), refetchInterval: 60_000 })

export interface LaneResult {
  lane: MyDayLane
  rows: CaseRow[] | null
  error: string | null
  servedAt: number | null
}

/** Load the complete lane. A partial first page must never be presented as the whole of "My day". */
export async function loadLane(lane: MyDayLane, ownerId: string): Promise<LaneResult> {
  try {
    const rows: CaseRow[] = []
    const seenCursors = new Set<string>()
    let cursor: string | null = null
    let servedAt: number | null = null

    do {
      const params = new URLSearchParams({ owner: ownerId, limit: '200' })
      if (cursor) params.set('cursor', cursor)
      const page = await getJson<PageCaseRow>(`/queue/lanes/${lane}?${params.toString()}`)
      rows.push(...page.data.items)
      if (page.servedAt !== null) servedAt = servedAt === null ? page.servedAt : Math.min(servedAt, page.servedAt)

      const next = page.data.next_cursor ?? null
      if (next && seenCursors.has(next)) throw new ApiError(502, 'repeated_cursor', 'Patchbay returned a repeated queue cursor.')
      if (next) seenCursors.add(next)
      cursor = next
    } while (cursor)

    return { lane, rows, error: null, servedAt }
  } catch (e) {
    return { lane, rows: null, error: describeError(e), servedAt: null }
  }
}

export async function loadUnassignedLane(lane: MyDayLane): Promise<LaneResult> {
  try {
    const rows: CaseRow[] = []
    const seenCursors = new Set<string>()
    let cursor: string | null = null
    let servedAt: number | null = null
    do {
      const params = new URLSearchParams({ unassigned: 'true', limit: '200' })
      if (cursor) params.set('cursor', cursor)
      const page = await getJson<PageCaseRow>(`/queue/lanes/${lane}?${params.toString()}`)
      rows.push(...page.data.items)
      if (page.servedAt !== null) servedAt = servedAt === null ? page.servedAt : Math.min(servedAt, page.servedAt)
      const next = page.data.next_cursor ?? null
      if (next && seenCursors.has(next)) throw new ApiError(502, 'repeated_cursor', 'Patchbay returned a repeated queue cursor.')
      if (next) seenCursors.add(next)
      cursor = next
    } while (cursor)
    return { lane, rows, error: null, servedAt }
  } catch (e) {
    return { lane, rows: null, error: describeError(e), servedAt: null }
  }
}

export const myLanesQuery = (ownerId: string | undefined) =>
  queryOptions({
    ...read,
    queryKey: ['my-day', ownerId],
    enabled: Boolean(ownerId),
    queryFn: async (): Promise<LaneResult[]> => Promise.all(MY_DAY_LANES.map((lane) => loadLane(lane, ownerId ?? ''))),
  })

export const unassignedLanesQuery = () =>
  queryOptions({
    ...read,
    queryKey: ['my-day', 'unassigned'],
    queryFn: async (): Promise<LaneResult[]> => Promise.all(MY_DAY_LANES.map((lane) => loadUnassignedLane(lane))),
  })

/** Phone / IT / MACD defaults for a manual time entry (read-only). */
export const timePromptQuery = (caseId: string) =>
  queryOptions({
    ...read,
    queryKey: ['case', caseId, 'time-prompt'],
    queryFn: () => getJson<TimeDefaultsOut>(`/cases/${encodeURIComponent(caseId)}/time-prompt`),
  })

export const caseDetailQuery = (id: string) =>
  queryOptions({ ...read, queryKey: ['case', id], queryFn: () => getJson<CaseDetail>(`/cases/${encodeURIComponent(id)}`) })

export const threadsQuery = (caseId: string) =>
  queryOptions({
    ...read,
    queryKey: ['case', caseId, 'threads'],
    queryFn: () => getJson<ThreadOut[]>(`/cases/${encodeURIComponent(caseId)}/threads`),
  })

export const turnsQuery = (threadId: string) =>
  queryOptions({
    ...read,
    queryKey: ['thread', threadId, 'turns'],
    queryFn: () => getJson<ThreadTurns>(`/threads/${encodeURIComponent(threadId)}/turns`),
  })

export const sendStateQuery = (threadId: string) =>
  queryOptions({
    ...read,
    queryKey: ['thread', threadId, 'send-state'],
    queryFn: () => getJson<SendState>(`/threads/${encodeURIComponent(threadId)}/send-state`),
  })

// ---- writes ---------------------------------------------------------------------------------------------

export const logTime = (body: TimeEntryIn) => writeJson<TimeEntryOut>('POST', '/time', body)
export const setNextMove = (caseId: string, body: NextMoveIn) =>
  writeJson<NextMoveOut>('PUT', `/cases/${encodeURIComponent(caseId)}/next-move`, body)
export const takeCase = (caseId: string, ownerUserId: string) =>
  writeJson<CaseRow[]>('POST', '/queue/bulk', {
    case_ids: [caseId],
    action: 'assign',
    owner_user_id: ownerUserId,
    plan_id: globalThis.crypto.randomUUID(),
    initiated_by: 'human',
  })
export const composeCheck = (body: { thread_id: string; body_html: string }) => writeJson<GuardVerdict>('POST', '/compose/check', body)
export const sendReply = (body: SendIn) => writeJson<SendOut>('POST', '/send', body)
export const buildCaseSummary = (caseId: string) =>
  writeJson<RileyCaseSummary>('POST', `/cases/${encodeURIComponent(caseId)}/summary`, {}, 190_000)
